import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  Mail, X, Filter, Loader2, Clock, CheckCircle2, XCircle, AlertCircle, Ban,
  Send, Building2, User, Briefcase, CalendarDays, Shield, ChevronDown, Eye
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { useModalLayer } from '../../lib/modalStack';
import { notifyToast } from '../../lib/toastStore';

interface AdminInvitationsManagerProps {
  canEdit?: boolean;
}

export default function AdminInvitationsManager({ canEdit = true }: AdminInvitationsManagerProps = {}) {
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployer, setFilterEmployer] = useState('');
  const [filterJob, setFilterJob] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [selectedInvite, setSelectedInvite] = useState<any | null>(null);
  const inviteZ = useModalLayer(!!selectedInvite);

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  useEffect(() => {
    setLoading(true);
    safeFetch('/api/admin/invitations')
      .then((d: any) => setInvitations(d.invitations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = invitations;
    if (filterStatus) list = list.filter((i) => (i.status || '').toLowerCase() === filterStatus.toLowerCase());
    if (filterEmployer) list = list.filter((i) => (i.employerName || '').toLowerCase().includes(filterEmployer.toLowerCase()));
    if (filterJob) list = list.filter((i) => (i.jobTitle || '').toLowerCase().includes(filterJob.toLowerCase()));
    if (filterDate) {
      const d = new Date(filterDate).toDateString();
      list = list.filter((i) => new Date(i.createdAt).toDateString() === d);
    }
    return list;
  }, [invitations, filterStatus, filterEmployer, filterJob, filterDate]);

  const statusMeta: Record<string, { color: string; icon: React.ReactNode }> = {
    pending: { color: 'bg-amber-50 text-amber-700 border-amber-100', icon: <Clock className="h-3 w-3" /> },
    accepted: { color: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: <CheckCircle2 className="h-3 w-3" /> },
    declined: { color: 'bg-red-50 text-red-700 border-red-100', icon: <XCircle className="h-3 w-3" /> },
    expired: { color: 'bg-slate-50 text-slate-600 border-slate-100', icon: <AlertCircle className="h-3 w-3" /> },
    cancelled: { color: 'bg-slate-50 text-slate-600 border-slate-100', icon: <Ban className="h-3 w-3" /> },
  };

  const handleAction = async (id: string, action: 'cancel' | 'accept' | 'decline') => {
    setActionBusy(id);
    try {
      await safeFetch(`/api/admin/invitations/${id}/${action}`, { method: 'POST' });
      setInvitations((prev) =>
        prev.map((i) =>
          i.id === id
            ? {
                ...i,
                status: action === 'cancel' ? 'cancelled' : action,
                respondedAt: action === 'cancel' ? undefined : new Date().toISOString(),
              }
            : i
        )
      );
      showToast(`Invitation ${action === 'cancel' ? 'cancelled' : action}ed successfully`, 'success');
    } catch (err: any) {
      showToast(err.message || 'Action failed', 'error');
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-black text-slate-900 flex items-center gap-2">
          <Shield className="h-4 w-4 text-brand" />
          Invitation Management
        </h1>
        <p className="text-[11px] text-slate-500">{filtered.length} invitation{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-wrap gap-2 items-center">
        <Filter className="h-3.5 w-3.5 text-slate-400" />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none ">
          <option value="">All Statuses</option>
          {['pending', 'accepted', 'declined', 'expired', 'cancelled'].map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Employer..."
          value={filterEmployer}
          onChange={(e) => setFilterEmployer(e.target.value)}
          className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none "
        />
        <input
          type="text"
          placeholder="Job..."
          value={filterJob}
          onChange={(e) => setFilterJob(e.target.value)}
          className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none "
        />
        <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none " />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-8 gap-4 px-5 py-3 bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          <span>Applicant</span>
          <span>Job / Post</span>
          <span>Program</span>
          <span>Employer</span>
          <span>Date Sent</span>
          <span>Status</span>
          <span>Responded</span>
          <span className="text-right">Actions</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
            <span className="ml-2 text-xs text-slate-500">Loading invitations...</span>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {filtered.map((inv) => {
              const meta = statusMeta[inv.status] || statusMeta.pending;
              const busy = actionBusy === inv.id;
              return (
                <div key={inv.id} className="grid grid-cols-8 gap-4 px-5 py-3 items-center hover:bg-slate-50 transition">
                  <span className="text-xs font-medium text-slate-800 truncate flex items-center gap-1">
                    <User className="h-3 w-3 text-slate-400 shrink-0" />
                    {inv.applicantName || '—'}
                  </span>
                  <span className="text-xs text-slate-600 truncate flex items-center gap-1">
                    <Briefcase className="h-3 w-3 text-slate-400 shrink-0" />
                    {inv.jobTitle || '—'}
                  </span>
                  <span className="text-xs text-slate-600 truncate">{inv.programType || inv.programName || '—'}</span>
                  <span className="text-xs text-slate-600 truncate flex items-center gap-1">
                    <Building2 className="h-3 w-3 text-slate-400 shrink-0" />
                    {inv.employerName || '—'}
                  </span>
                  <span className="text-[11px] text-slate-500">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</span>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold border w-fit ${meta.color}`}>
                    {meta.icon}
                    {(inv.status || 'pending').charAt(0).toUpperCase() + (inv.status || 'pending').slice(1)}
                  </span>
                  <span className="text-[11px] text-slate-500">{inv.respondedAt ? new Date(inv.respondedAt).toLocaleDateString() : '—'}</span>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setSelectedInvite(inv)} className="px-2 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-lg hover:bg-blue-100 cursor-pointer transition flex items-center gap-1"><Eye className="h-3 w-3" /> Details</button>
                    {inv.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAction(inv.id, 'accept')}
                          disabled={busy}
                          className="px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-lg hover:bg-emerald-100 disabled:opacity-50 cursor-pointer transition flex items-center gap-1"
                        >
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                          Accept
                        </button>
                        <button
                          onClick={() => handleAction(inv.id, 'decline')}
                          disabled={busy}
                          className="px-2 py-1 bg-red-50 text-red-700 text-[10px] font-bold rounded-lg hover:bg-red-100 disabled:opacity-50 cursor-pointer transition flex items-center gap-1"
                        >
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                          Decline
                        </button>
                        <button
                          onClick={() => handleAction(inv.id, 'cancel')}
                          disabled={busy}
                          className="px-2 py-1 bg-slate-50 text-slate-600 text-[10px] font-bold rounded-lg hover:bg-slate-100 disabled:opacity-50 cursor-pointer transition flex items-center gap-1"
                        >
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                          Cancel
                        </button>
                      </>
                    )}
                    {inv.status !== 'pending' && <span className="text-[11px] text-slate-500">—</span>}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="p-6 text-center text-xs text-slate-500">No invitations found.</div>
            )}
          </div>
        )}
      </div>



      {selectedInvite && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in" style={{ zIndex: inviteZ }} onClick={() => setSelectedInvite(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-black text-slate-900">Invitation Details</h3>
                <p className="text-[11px] text-slate-500">Job and application summary side by side</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setSelectedInvite(null)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100 overflow-y-auto flex-1 max-h-[75vh]">
              <section className="p-5 space-y-4">
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Job Details</h4>
                {[['Job title', selectedInvite.jobTitle], ['Job summary', selectedInvite.jobSummary], ['Employer', selectedInvite.employerName], ['Employer email', selectedInvite.employerEmail], ['Job ID', selectedInvite.jobId], ['Status', selectedInvite.status], ['Sent', selectedInvite.createdAt ? new Date(selectedInvite.createdAt).toLocaleString() : '—']].map(([label, value]) => (
                  <div key={label as string} className="rounded-xl border border-slate-100 bg-slate-50 p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p><p className="text-sm font-bold text-slate-800 mt-1 break-words">{value || '—'}</p></div>
                ))}
              </section>
              <section className="p-5 space-y-4">
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><User className="h-4 w-4" /> Application / Applicant</h4>
                {[['Applicant', selectedInvite.applicantName], ['Applicant email', selectedInvite.applicantEmail], ['Application ID', selectedInvite.applicantId || selectedInvite.applicationId], ['Program', selectedInvite.programType || selectedInvite.programName], ['Responded', selectedInvite.respondedAt ? new Date(selectedInvite.respondedAt).toLocaleString() : '—'], ['Response note', selectedInvite.responseNote]].map(([label, value]) => (
                  <div key={label as string} className="rounded-xl border border-slate-100 bg-white p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p><p className="text-sm font-bold text-slate-800 mt-1 break-words">{value || '—'}</p></div>
                ))}
              </section>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toasts render via the global root-level notification layer. */}
    </div>
  );
}
